/**
 * Withdrawal Contracts - Zod schemas matching PostgreSQL exactly
 *
 * DATABASE-FIRST: All schemas derived from MCP postgres queries on 2026-01-02
 *
 * Table: withdrawals (19 columns)
 * Key functions: validate_withdrawal_v2, create_withdrawal_v2, log_withdrawal_event_v2
 */
import { z } from 'zod';
import { bigintString, numericString, timestampString, withdrawalStatusSchema, withdrawalReferenceSchema, withdrawalLogTypeSchema, cardNumberSchema, successResponseSchema, } from '../common.js';
// =============================================================================
// DATABASE ENTITY: withdrawals table
// Source: SELECT * FROM information_schema.columns WHERE table_name = 'withdrawals'
// =============================================================================
/**
 * Full withdrawal record as stored in PostgreSQL
 */
export const withdrawalEntitySchema = z.object({
    reference_number: z.string(), // varchar NOT NULL (PK)
    user_telegram_id: bigintString, // bigint NOT NULL → string in JSON
    original_amount: z.number().int(), // integer NOT NULL
    original_bonus: z.number().int().nullable(), // integer NULL (default 0)
    amount: z.number().int(), // integer NOT NULL (final payout)
    status: withdrawalStatusSchema, // varchar NOT NULL (CHECK constraint)
    completed_at: timestampString, // timestamptz NULL
    assigned_to: z.string().nullable(), // varchar NULL (FK to staff.identifier)
    card_number: z.string().nullable(), // varchar NULL
    provider_hash: z.string().nullable(), // varchar NULL
    provider_transaction_id: z.string().nullable(), // varchar NULL
    is_manual: z.boolean().nullable(), // boolean NULL (default false)
    usdt_amount: numericString, // numeric NULL → string in JSON
    exchange_rate: numericString, // numeric NULL → string in JSON
    commission: numericString, // numeric NULL → string in JSON
    photo_url: z.string().nullable(), // text NULL
    resend_used: z.boolean().nullable(), // boolean NULL (default false)
    created_at: timestampString, // timestamptz NULL (default now())
    referee_identifier: z.string().nullable(), // varchar NULL (FK to referees.identifier)
});
// =============================================================================
// STORED PROCEDURE: validate_withdrawal_v2
// Args: p_telegram_id bigint, p_requested_amount integer, p_current_balance integer
// Returns: TABLE(allowed boolean, message text, minimum_required integer, bonus_to_deduct integer, actual_payout integer)
// =============================================================================
export const validateWithdrawalInputSchema = z.object({
    telegram_id: bigintString,
    requested_amount: z.number().int().positive(),
    current_balance: z.number().int().nonnegative(),
});
export const validateWithdrawalResultSchema = z.object({
    allowed: z.boolean(),
    message: z.string(),
    minimum_required: z.number().int(),
    bonus_to_deduct: z.number().int(),
    actual_payout: z.number().int(),
});
// =============================================================================
// STORED PROCEDURE: create_withdrawal_v2
// Args: p_telegram_id bigint, p_original_amount integer, p_bonus_to_deduct integer, p_card_number varchar
// Returns: TABLE(success boolean, message text, reference_number varchar, amount integer)
// =============================================================================
export const createWithdrawalInputSchema = z.object({
    telegram_id: bigintString,
    original_amount: z.number().int().positive(),
    bonus_to_deduct: z.number().int().nonnegative(),
    card_number: z.string(),
});
export const createWithdrawalResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reference_number: z.string(),
    amount: z.number().int(),
});
// =============================================================================
// STORED PROCEDURE: log_withdrawal_event_v2
// Args: p_withdrawal_ref, p_log_type, [optional params...]
// Returns: TABLE(success boolean, message text, log_id integer)
// =============================================================================
export const logWithdrawalEventInputSchema = z.object({
    withdrawal_ref: withdrawalReferenceSchema,
    log_type: withdrawalLogTypeSchema,
    cascade_stats: z.record(z.unknown()).nullable().optional(),
    api_response: z.record(z.unknown()).nullable().optional(),
    provider_hash: z.string().nullable().optional(),
    provider_transaction_id: z.string().nullable().optional(),
    photo_url: z.string().nullable().optional(),
    staff_id: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    previous_values: z.record(z.unknown()).nullable().optional(),
    new_values: z.record(z.unknown()).nullable().optional(),
    result: z.string().nullable().optional(),
});
export const logWithdrawalEventResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    log_id: z.number().int(),
});
// =============================================================================
// API REQUESTS
// =============================================================================
/**
 * POST /api/wallet/withdrawal
 * Request body for initiating a withdrawal
 */
export const withdrawalRequestSchema = z.object({
    amount: z.number().positive('Amount must be positive'),
    cardNumber: cardNumberSchema,
});
// =============================================================================
// API RESPONSES
// =============================================================================
/**
 * POST /api/wallet/withdrawal - Success response
 */
export const withdrawalInitResponseDataSchema = z.object({
    message: z.string(),
    referenceNumber: withdrawalReferenceSchema,
    originalAmount: z.number().int(),
    bonusDeducted: z.number().int(),
    finalAmount: z.number().int(),
    status: z.literal('pending'),
});
export const withdrawalInitResponseSchema = successResponseSchema(withdrawalInitResponseDataSchema);
// =============================================================================
// ERROR RESPONSES
// =============================================================================
/**
 * POST /api/wallet/withdrawal - Error: validation failed
 */
export const withdrawalValidationErrorSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    data: z.object({
        minimumRequired: z.number().int(),
        currentBalance: z.number().int(),
    }),
});
//# sourceMappingURL=withdrawal.js.map